import { ChartNoAxesColumn, Ellipsis, House, Plus, Wallet, type LucideIcon } from 'lucide-react';
import { Link, matchPath, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Wordmark } from '../ui';
import { scrollToTop, useScreenLocation } from './navigation';

export type TabKey = 'home' | 'analytics' | 'budgets' | 'more';

interface Tab {
  key: TabKey;
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

const LEFT: Tab[] = [
  { key: 'home', to: '/', labelKey: 'nav.home', icon: House },
  { key: 'analytics', to: '/analytics', labelKey: 'nav.analytics', icon: ChartNoAxesColumn },
];
const RIGHT: Tab[] = [
  { key: 'budgets', to: '/budgets', labelKey: 'nav.budgets', icon: Wallet },
  { key: 'more', to: '/more', labelKey: 'nav.more', icon: Ellipsis },
];

/**
 * Home · Analytics · + · Budgets · More. Bottom bar on phones, left rail
 * with the wordmark on wide screens. `activeKey` pins the active tab for
 * design prototypes; the real app derives it from the URL.
 */
export function TabBar({ activeKey }: { activeKey?: TabKey }) {
  const { t } = useTranslation();
  const location = useLocation();
  const screen = useScreenLocation();
  const current =
    activeKey ?? [...LEFT, ...RIGHT].find((tab) => matchPath({ path: tab.to, end: tab.to === '/' }, screen.pathname))?.key;

  const renderTab = (tab: Tab) => {
    const Icon = tab.icon;
    const active = tab.key === current;
    return (
      <Link
        key={tab.key}
        to={tab.to}
        className="tab"
        aria-current={active ? 'page' : undefined}
        onClick={(e) => {
          // Re-tapping the current tab scrolls it to the top instead of re-navigating.
          if (active && location.pathname === tab.to) {
            e.preventDefault();
            scrollToTop();
          }
        }}
      >
        <Icon aria-hidden size={22} strokeWidth={active ? 2.25 : 1.75} fill={active && tab.key === 'home' ? 'currentColor' : 'none'} />
        <span className="tab-label">{t(tab.labelKey)}</span>
      </Link>
    );
  };

  return (
    <nav className="tabbar" aria-label={t('nav.main')}>
      <span className="tabbar-wordmark">
        <Wordmark />
      </span>
      {LEFT.map(renderTab)}
      <Link to="/add" state={{ background: location }} className="tab-add" aria-label={t('nav.add')} aria-keyshortcuts="N" title={t('nav.addShortcut')}>
        <Plus aria-hidden size={24} strokeWidth={2} />
        <span className="tab-add-label" aria-hidden>
          {t('nav.add')}
        </span>
      </Link>
      {RIGHT.map(renderTab)}
    </nav>
  );
}
